import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { API_URL } from "config";

/* ─────────────────────────────────────────
   1. CONTEXT SHAPE
───────────────────────────────────────── */
const AuthContext = createContext({
  isLoggedIn: false,
  isAuthReady: false,
  allowedOvertime: false,
  refreshAccess: async () => {},
  login: () => { },
  logout: () => { },
  user: null,
  roles: [],
  permissions: [],
  accessScopes: {},
  accessUserScope: "self",
  userPermission: [], // ✅ NEW
});

const sameList = (left, right) =>
  left.length === right.length && left.every((item, index) => item === right[index]);

/* ─────────────────────────────────────────
   2. PROVIDER
───────────────────────────────────────── */
export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [allowedOvertime, setAllowedOvertime] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [accessScopes, setAccessScopes] = useState({});
  const [accessUserScope, setAccessUserScope] = useState("self");
  const [userPermission, setUserPermission] = useState([]); // ✅ NEW



  /* ---------- Logout ---------- */
  const logout = useCallback(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      axios
        .post(
          `${API_URL}/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        )
        .catch(() => {});
    }

    // 🔥 SAFE DISCONNECT (NO getEcho CALL)
    if (window.__ECHO__) {
      console.log("🔌 Disconnecting Echo...");
      window.__ECHO__.disconnect();
    }

    // 🔥 reset globals
    window.__ECHO__ = null;
    window.__ECHO_DEBUG_BOUND__ = false;

    // 🔥 MASTER LOCK REMOVE (VERY IMPORTANT)
    localStorage.removeItem("echo_master");


    localStorage.removeItem("authToken");
    localStorage.removeItem("accessRoles");
    localStorage.removeItem("role");
    localStorage.removeItem("userRole");
    localStorage.removeItem("permissions");
    localStorage.removeItem("userPermission");
    localStorage.removeItem("accessScopes");
    localStorage.removeItem("accessUserScope");
    localStorage.removeItem("visibleFilters");
    localStorage.removeItem("permissionOverrides");
    localStorage.removeItem("name");
    localStorage.removeItem("user");

    setIsLoggedIn(false);
    setUser(null);
    setRoles([]);
    setPermissions([]);
    setAccessScopes({});
    setAccessUserScope("self");
    setUserPermission([]);
  }, []);

  /* ---------- Login ---------- */
  const login = useCallback(async (email, password) => {
    logout();

    try {
      const { data } = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      localStorage.setItem("authToken", data.token);
      setIsLoggedIn(true);
      return true;
    } catch (err) {
      console.error("Login failed:", err);
      logout();
      return false;
    }
  }, [logout]);

  /* ---------- Fetch Current User ---------- */
  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No token");

      const { data } = await axios.get(`${API_URL}/user`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      let accessData = null;
      try {
        const accessResponse = await axios.get(`${API_URL}/me/access`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        accessData = accessResponse.data;
      } catch (accessError) {
        console.warn("Access profile unavailable:", accessError?.message || accessError);
      }

      /**
       * API RESPONSE
       * data.user
       * data.roles
       * data.permissions
       */

      // console.log(data);
      console.log("👤 USER DATA:", data);
      setUser(data.user || null);
      setRoles(data.roles || []);
      setAccessScopes(accessData?.scopes || {});
      setAccessUserScope(accessData?.user_scope || "self");
      // The access profile is authoritative; merging role grants could restore a denied action.
      const mergedPermissions = Array.from(new Set(
        Array.isArray(accessData?.permissions) ? accessData.permissions : (data.permissions || [])
      ));
      localStorage.setItem("permissionOverrides", JSON.stringify(accessData?.permission_overrides || {}));

      setPermissions(mergedPermissions);
      // console.log(data?.attendance_settings?.overtime_allowed)
      setAllowedOvertime(data?.attendance_settings?.overtime_allowed);

      // ✅ MAIN REQUIREMENT
      setUserPermission(mergedPermissions);

      setIsLoggedIn(true);

      // Persist
      if (data.user?.name) {
        localStorage.setItem("name", data.user.name);
      }

      localStorage.setItem("accessRoles", JSON.stringify(data.roles || []));
      localStorage.setItem("permissions", JSON.stringify(mergedPermissions));
      localStorage.setItem("user", JSON.stringify(data.user || {}));
      localStorage.setItem("accessScopes", JSON.stringify(accessData?.scopes || {}));
      localStorage.setItem("accessUserScope", accessData?.user_scope || "self");
      localStorage.setItem("visibleFilters", JSON.stringify(accessData?.visible_filters || []));

      localStorage.setItem("userPermission", JSON.stringify(mergedPermissions));
    } catch (err) {
      console.error("❌ Auth error:", err);
      logout();
    } finally {
      setIsAuthReady(true);
    }
  }, [logout]);

  const refreshAccess = useCallback(async () => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/me/access`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      if (localStorage.getItem("authToken") !== token || !Array.isArray(data?.permissions)) return;
      const next = Array.from(new Set(data.permissions));
      const nextScopes = data.scopes || {};
      const nextUserScope = data.user_scope || "self";
      localStorage.setItem("permissionOverrides", JSON.stringify(data.permission_overrides || {}));
      localStorage.setItem("permissions", JSON.stringify(next));
      localStorage.setItem("userPermission", JSON.stringify(next));
      localStorage.setItem("accessScopes", JSON.stringify(nextScopes));
      localStorage.setItem("accessUserScope", nextUserScope);
      localStorage.setItem("visibleFilters", JSON.stringify(data.visible_filters || []));
      setPermissions((current) => (sameList(current, next) ? current : next));
      setUserPermission((current) => (sameList(current, next) ? current : next));
      setAccessScopes((current) =>
        JSON.stringify(current) === JSON.stringify(nextScopes) ? current : nextScopes
      );
      setAccessUserScope(nextUserScope);
    } catch (error) {
      if (localStorage.getItem("authToken") === token && error?.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (!isLoggedIn || !isAuthReady) return undefined;
    const refresh = () => {
      if (document.visibilityState !== "hidden") refreshAccess();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("access-permissions-changed", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("access-permissions-changed", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [isLoggedIn, isAuthReady, refreshAccess]);

  /* ---------- Init ---------- */
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    token ? fetchUser() : setIsAuthReady(true);
  }, [fetchUser]);

  /* ---------- Keep Alive ---------- */
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const token = localStorage.getItem("authToken");
  //     if (!token) return;

  //     axios
  //       .get(`${API_URL}/ping`, {
  //         headers: { Authorization: `Bearer ${token}` },
  //         withCredentials: true,
  //       })
  //       .catch(() => logout());
  //   }, 10 * 60 * 1000);

  //   return () => clearInterval(interval);
  // }, [logout]);

  /* ---------- Context Value ---------- */
  const value = useMemo(
    () => ({
      isLoggedIn,
      isAuthReady,
      login,
      logout,
      user,
      roles,
      permissions,
      accessScopes,
      accessUserScope,
      refreshAccess,
      userPermission, // ✅ EXPOSED
      allowedOvertime
    }),
    [
      isLoggedIn,
      allowedOvertime,
      isAuthReady,
      login,
      logout,
      user,
      roles,
      permissions,
      accessScopes,
      accessUserScope,
      userPermission,
      refreshAccess,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/* ─────────────────────────────────────────
   3. HOOK
───────────────────────────────────────── */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
